import React, { useState } from "react";
import { Braces, Download, Copy, Check, ChevronRight, ChevronDown, ShieldCheck, ShieldX } from "lucide-react";

export default function Inspector({ event }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  if (!event) {
    return (
      <div className="bg-panel border border-line rounded-xl p-8 text-center text-muted">
        <Braces size={32} className="mx-auto mb-3 opacity-50" />
        Enrich some text in the Live Studio to see the live metadata envelope here.
      </div>
    );
  }
  const meta = event.metadata || {};
  const prov = meta.provenance?.pv_01;
  const copy = async () => {
    try {
      setError(""); await navigator.clipboard.writeText(JSON.stringify(meta, null, 2));
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    } catch (_) { setError("Clipboard unavailable. You can download the JSON instead."); }
  };
  const download = () => {
    const blob = new Blob([JSON.stringify(event, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wisprlink_event_rev${event.rev}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
      <aside className="space-y-3">
        <div className="bg-panel border border-line rounded-xl p-4">
          <h3 className="font-display font-semibold mb-3">Event</h3>
          <Row k="rev" v={event.rev} />
          <Row k="validation" v={event.validation?.valid ? "✓ valid" : `✗ ${event.validation?.errors?.length} errors`} ok={event.validation?.valid} />
          <Row k="embedding" v={event.embedding_backend} />
          <Row k="llm rerank" v={event.llm_rerank_active ? "active" : "off"} />
        </div>
        {prov && (
          <div className="bg-panel border border-line rounded-xl p-4" data-testid="provenance-box">
            <h3 className="font-display font-semibold mb-3">Provenance</h3>
            <Row k="source" v={prov.source} />
            <Row k="adapter" v={prov.input_adapter} />
            <Row k="insert chars" v={prov.insert_chars} />
            <Row k="gap ms" v={prov.insert_gap_ms} />
            <Row k="sha256" v={prov.text_sha256.slice(0, 16) + "…"} />
          </div>
        )}
        {event.invariant && (
          <div className={`rounded-xl p-4 border ${event.invariant.intact ? "border-lime/30 bg-lime/5" : "border-rose/30 bg-rose/5"}`}>
            <div className={`flex items-center gap-2 font-semibold ${event.invariant.intact ? "text-lime" : "text-rose"}`}>
              {event.invariant.intact ? <ShieldCheck size={16} /> : <ShieldX size={16} />}
              Invariant {event.invariant.intact ? "holds" : "broken"}
            </div>
            <div className="text-[11px] font-mono text-muted mt-2 break-all">
              raw: {event.invariant.original_sha256.slice(0, 24)}…<br />
              rebuilt: {event.invariant.reconstructed_sha256.slice(0, 24)}…
            </div>
          </div>
        )}
      </aside>

      <div className="bg-panel border border-line rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h3 className="font-display font-semibold">Metadata Envelope</h3>
          <span className="text-xs text-muted font-mono">schema-validated · nested by namespace</span>
          <div className="ml-auto flex gap-2">
            <button data-testid="copy-json-btn" onClick={copy} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-line hover:border-teal">
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "copied" : "copy"}
            </button>
            <button data-testid="download-json-btn" onClick={download} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-line hover:border-teal">
              <Download size={13} /> json
            </button>
          </div>
        </div>
        {!event.validation?.valid && (
          <div className="mb-3 text-xs text-rose font-mono bg-rose/10 rounded p-2">
            {event.validation?.errors?.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}
        {error && <div data-testid="inspector-copy-error" role="alert" className="text-rose text-xs mb-3">{error}</div>}
        <div className="space-y-1.5">
          {Object.entries(meta).map(([ns, features]) => (
            <Namespace key={ns} ns={ns} features={features} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, ok }) {
  return (
    <div data-testid={`inspector-${k.replace(/\s/g, "-")}`} className="flex items-center justify-between text-xs py-1 font-mono">
      <span className="text-muted">{k}</span>
      <span className={ok === false ? "text-rose" : ok === true ? "text-lime" : "text-fg"}>{String(v)}</span>
    </div>
  );
}

function Namespace({ ns, features }) {
  const [open, setOpen] = useState(["links", "deixis", "provenance"].includes(ns));
  const count = Object.keys(features).length;
  return (
    <div className="border border-line rounded-lg overflow-hidden" data-testid={`ns-${ns}`}>
      <button data-testid={`namespace-toggle-${ns}`} onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 bg-panel2 hover:bg-panel2/70 text-left">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="font-mono text-sm text-amber">{ns}</span>
        <span className="text-[11px] text-muted">{count}</span>
      </button>
      {open && (
        <pre className="text-[11px] font-mono text-fg/80 p-3 overflow-auto bg-ink max-h-80">
{JSON.stringify(features, null, 2)}
        </pre>
      )}
    </div>
  );
}
